import type { BankAccount, CryptoWallet, UpiAccount } from '@/types/Account';

/** Smallest withdrawal in coins: the same rule the /withdraw page's form uses. */
export const MIN_WITHDRAW_AMOUNT = 100;

type AccountBase = { id: string; label: string };

/** A saved account the user can withdraw to (from `/bank`, `/upi` and `/crypto`). */
export type WithdrawAccount =
  | (AccountBase & { kind: 'bank'; account: BankAccount })
  | (AccountBase & { kind: 'upi'; account: UpiAccount })
  | (AccountBase & { kind: 'crypto'; account: CryptoWallet; rate?: number });

const lastFour = (value?: string | null) => (value ? `•••• ${value.slice(-4)}` : '');

export const shortAddress = (address: string) =>
  address.length > 14 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;

const positive = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export const buildWithdrawAccounts = (
  banks: BankAccount[] = [],
  upis: UpiAccount[] = [],
  wallets: CryptoWallet[] = []
): WithdrawAccount[] => [
  ...banks.map(
    (account): WithdrawAccount => ({
      kind: 'bank',
      id: `bank-${account.id}`,
      label: [account.bankName || 'Bank', lastFour(account.accountNumber)].join(' ').trim(),
      account,
    })
  ),
  ...upis.map(
    (account): WithdrawAccount => ({
      kind: 'upi',
      id: `upi-${account.id}`,
      label: account.upiId,
      account,
    })
  ),
  ...wallets.map(
    (account): WithdrawAccount => ({
      kind: 'crypto',
      id: `crypto-${account.id}`,
      label: `${account.network?.paymentOptions?.[0]?.displayName || account.chain || 'USDT'} · ${shortAddress(account.walletAddress)}`,
      rate: positive(account.network?.paymentOptions?.[0]?.usdRate),
      account,
    })
  ),
];
